<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <h4 class="text-capitalize fw-500 breadcrumb-title">
                            <i class="fas fa-money-check"></i> Konfirmasi Manual Transfer
                        </h4>
                        <div class="action-btn">
                            <button @click="load" class="btn btn-light">
                                <i class="fas fa-sync"></i> Reload
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Stats row -->
            <div class="row mb-3">
                <div class="col-md-4">
                    <div class="card p-3 text-center" style="cursor:pointer;" @click="setStatus('pending')">
                        <small class="text-muted">Pending</small>
                        <h3 class="mb-0 text-warning">{{ counts.pending || 0 }}</h3>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="card p-3 text-center" style="cursor:pointer;" @click="setStatus('approved')">
                        <small class="text-muted">Approved</small>
                        <h3 class="mb-0 text-success">{{ counts.approved || 0 }}</h3>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="card p-3 text-center" style="cursor:pointer;" @click="setStatus('rejected')">
                        <small class="text-muted">Rejected</small>
                        <h3 class="mb-0 text-danger">{{ counts.rejected || 0 }}</h3>
                    </div>
                </div>
            </div>

            <!-- Filter -->
            <div class="card p-3 mb-3">
                <div class="row align-items-end">
                    <div class="col-md-3 mb-2">
                        <label>Status</label>
                        <select v-model="filter.status" @change="load" class="form-control">
                            <option value="">Semua</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                    <div class="col-md-3 mb-2">
                        <label>Mode</label>
                        <select v-model="filter.mode" @change="load" class="form-control">
                            <option value="">Semua</option>
                            <option value="buy_package">Beli Paket</option>
                            <option value="topup">Top Up Saldo</option>
                            <option value="topup_chatgpt">Top Up ChatGPT</option>
                            <option value="addon">Add-on</option>
                            <option value="shop_order">Shop Order</option>
                        </select>
                    </div>
                    <div class="col-md-3 mb-2">
                        <label>User ID</label>
                        <input v-model="filter.user_id" @keyup.enter="load" class="form-control" placeholder="user id">
                    </div>
                    <div class="col-md-3 mb-2">
                        <button class="btn btn-primary mr-2" @click="load">Cari</button>
                        <button class="btn btn-secondary" @click="resetFilter">Reset</button>
                    </div>
                </div>
            </div>

            <!-- Table -->
            <div class="card p-3">
                <div class="table-responsive">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Tanggal</th>
                                <th>User</th>
                                <th>Mode</th>
                                <th>Amount</th>
                                <th>Bank Tujuan</th>
                                <th>Pengirim</th>
                                <th>Status</th>
                                <th>Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="loading">
                                <td colspan="9" class="text-center text-muted">Loading...</td>
                            </tr>
                            <tr v-else-if="payments.length === 0">
                                <td colspan="9" class="text-center text-muted">
                                    <div>{{ emptyTableMessage }}</div>
                                    <div v-if="emptyTableHint" class="small mt-2">{{ emptyTableHint }}</div>
                                </td>
                            </tr>
                            <tr v-for="p in payments" :key="p.id">
                                <td>#PAY-{{ p.id }}</td>
                                <td>
                                    <small>{{ formatTime(p.created_at) }}</small>
                                </td>
                                <td>
                                    <div><strong>{{ p.user_username || p.user_email }}</strong></div>
                                    <div class="text-muted small">{{ p.user_email }}</div>
                                </td>
                                <td>
                                    <span class="badge bg-info">{{ modeLabel(p.payment_mode) }}</span>
                                    <div v-if="p.target_label" class="text-muted small">{{ p.target_label }}</div>
                                </td>
                                <td><strong>{{ formatRupiah(p.amount) }}</strong></td>
                                <td>
                                    <span v-if="p.bank_name_target">
                                        {{ p.bank_name_target }} <br>
                                        <code>{{ p.bank_account_number_target }}</code>
                                    </span>
                                    <span v-else class="text-muted">—</span>
                                </td>
                                <td>
                                    <div v-if="p.sender_name">{{ p.sender_name }}</div>
                                    <div v-if="p.sender_bank" class="text-muted small">{{ p.sender_bank }}{{ p.sender_account ? ' / ' + p.sender_account : '' }}</div>
                                </td>
                                <td>
                                    <span :class="statusBadge(p.status)">{{ p.status.toUpperCase() }}</span>
                                </td>
                                <td>
                                    <button @click="openDetail(p)" class="btn btn-sm btn-primary">
                                        <i class="fas fa-eye"></i> Detail
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div class="d-flex justify-content-between align-items-center mt-3">
                    <small class="text-muted">Total: {{ total }} | Page {{ page }} / {{ totalPages }}</small>
                    <div>
                        <button class="btn btn-sm btn-light mr-2" :disabled="page <= 1" @click="goPage(page - 1)">Prev</button>
                        <button class="btn btn-sm btn-light" :disabled="page >= totalPages" @click="goPage(page + 1)">Next</button>
                    </div>
                </div>
            </div>

            <!-- Detail modal -->
            <div v-if="detailOpen" class="custom-modal-overlay" @click.self="detailOpen = false">
                <div class="custom-modal-content">
                    <div class="card p-4">
                        <h5 class="mb-3">
                            <i class="fas fa-money-check"></i>
                            Detail #PAY-{{ selected.id }}
                            <span :class="statusBadge(selected.status)" class="ml-2">{{ selected.status.toUpperCase() }}</span>
                        </h5>

                        <div class="row">
                            <div class="col-md-7">
                                <table class="table table-sm">
                                    <tbody>
                                        <tr><th width="40%">Mode</th><td>{{ modeLabel(selected.payment_mode) }}</td></tr>
                                        <tr v-if="selected.target_label"><th>Untuk</th><td>{{ selected.target_label }}</td></tr>
                                        <tr><th>Amount</th><td><strong>{{ formatRupiah(selected.amount) }}</strong></td></tr>
                                        <tr><th>User</th><td>{{ selected.user_username }} ({{ selected.user_email }})</td></tr>
                                        <tr v-if="selected.user_phone"><th>Phone</th><td>{{ selected.user_phone }}</td></tr>
                                        <tr v-if="selected.bank_name_target"><th>Bank Tujuan</th><td>{{ selected.bank_name_target }} — <code>{{ selected.bank_account_number_target }}</code></td></tr>
                                        <tr v-if="selected.sender_name"><th>Nama Pengirim</th><td>{{ selected.sender_name }}</td></tr>
                                        <tr v-if="selected.sender_bank"><th>Bank Pengirim</th><td>{{ selected.sender_bank }}</td></tr>
                                        <tr v-if="selected.sender_account"><th>Rek Pengirim</th><td>{{ selected.sender_account }}</td></tr>
                                        <tr v-if="selected.transfer_date"><th>Tgl Transfer</th><td>{{ formatTime(selected.transfer_date) }}</td></tr>
                                        <tr><th>Tgl Submit</th><td>{{ formatTime(selected.created_at) }}</td></tr>
                                        <tr v-if="selected.user_note"><th>Catatan User</th><td><em>{{ selected.user_note }}</em></td></tr>
                                        <tr v-if="selected.admin_note"><th>Catatan Admin</th><td><em>{{ selected.admin_note }}</em></td></tr>
                                        <tr v-if="selected.verified_at"><th>Diverifikasi</th><td>{{ formatTime(selected.verified_at) }}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="col-md-5">
                                <label class="d-block mb-2"><strong>Bukti Transfer:</strong></label>
                                <a v-if="selected.proof_image_url" :href="selected.proof_image_url" target="_blank">
                                    <img :src="selected.proof_image_url" alt="bukti" style="max-width:100%; max-height:380px; border:1px solid #ddd;">
                                </a>
                                <div v-else class="text-muted">Tidak ada bukti</div>
                            </div>
                        </div>

                        <div v-if="selected.status === 'pending'" class="mt-3">
                            <label>Catatan (opsional untuk approve, wajib untuk reject)</label>
                            <textarea v-model="actionNote" class="form-control" rows="2" placeholder="Catatan admin..."></textarea>
                        </div>

                        <div class="d-flex gap-2 mt-3">
                            <button v-if="selected.status === 'pending'" @click="approvePayment"
                                :disabled="actionRunning" class="btn btn-success mr-2">
                                <i class="fas fa-check"></i> {{ actionRunning ? 'Memproses...' : 'Approve' }}
                            </button>
                            <button v-if="selected.status === 'pending'" @click="rejectPayment"
                                :disabled="actionRunning" class="btn btn-danger mr-2">
                                <i class="fas fa-times"></i> {{ actionRunning ? 'Memproses...' : 'Reject' }}
                            </button>
                            <button @click="detailOpen = false" class="btn btn-secondary">Tutup</button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
    data() {
        return {
            loading: false,
            actionRunning: false,
            payments: [],
            counts: { pending: 0, approved: 0, rejected: 0 },
            total: 0,
            page: 1,
            limit: 30,
            filter: { status: 'pending', mode: '', user_id: '' },
            /** First load: if no pending, show Semua so table matches summary cards */
            widenEmptyPendingOnce: true,
            detailOpen: false,
            selected: {},
            actionNote: '',
        };
    },
    computed: {
        totalPages() {
            return Math.max(1, Math.ceil(this.total / this.limit));
        },
        emptyTableMessage() {
            if (this.filter.status === 'pending') {
                return 'Tidak ada konfirmasi pending saat ini.';
            }
            if (this.filter.status) {
                return 'Tidak ada data untuk filter ini.';
            }
            return 'Tidak ada data';
        },
        emptyTableHint() {
            const hist = (this.counts.approved || 0) + (this.counts.rejected || 0);
            if (this.filter.status === 'pending' && hist > 0) {
                return 'Ada riwayat approved/rejected di kartu di atas — klik kartu tersebut atau ubah Status ke Semua.';
            }
            return '';
        },
    },
    mounted() {
        this.load();
    },
    methods: {
        notify(type, msg) {
            if (this.$toast) {
                this.$toast.open({ message: msg, type, duration: type === 'error' ? 6000 : 3000, position: 'top-right' });
            } else { window.alert(msg); }
        },
        errMsg(e, fb) {
            return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || fb;
        },
        formatRupiah(n) {
            return 'Rp ' + Number(n || 0).toLocaleString('id-ID');
        },
        formatTime(ms) {
            if (!ms) return '—';
            return moment(parseInt(ms)).format('DD MMM YYYY HH:mm');
        },
        modeLabel(m) {
            switch (m) {
                case 'buy_package': return 'Beli Paket';
                case 'topup': return 'Top Up Saldo';
                case 'topup_chatgpt': return 'Top Up ChatGPT';
                case 'addon': return 'Add-on';
                case 'shop_order': return 'Shop Order';
                default: return m;
            }
        },
        statusBadge(s) {
            if (s === 'approved') return 'badge bg-success';
            if (s === 'rejected') return 'badge bg-danger';
            return 'badge bg-warning text-dark';
        },
        setStatus(s) {
            this.filter.status = s;
            this.page = 1;
            this.load();
        },
        resetFilter() {
            this.filter = { status: '', mode: '', user_id: '' };
            this.page = 1;
            this.load();
        },
        goPage(p) {
            if (p < 1 || p > this.totalPages) return;
            this.page = p;
            this.load();
        },
        async load() {
            this.loading = true;
            try {
                const params = {
                    page: this.page,
                    limit: this.limit,
                    status: this.filter.status || '',
                    mode: this.filter.mode || '',
                };
                if (this.filter.user_id) params.user_id = this.filter.user_id;
                const r = await axios.get('admin/payment/manual', { params });
                if (r.data && r.data.success) {
                    this.payments = r.data.data.payments || [];
                    this.total = r.data.data.total || 0;
                    this.counts = r.data.data.counts || this.counts;
                    const hist = (this.counts.approved || 0) + (this.counts.rejected || 0);
                    if (
                        this.widenEmptyPendingOnce &&
                        this.filter.status === 'pending' &&
                        this.total === 0 &&
                        hist > 0 &&
                        !this.filter.mode &&
                        !this.filter.user_id
                    ) {
                        this.widenEmptyPendingOnce = false;
                        this.filter.status = '';
                        this.page = 1;
                        return this.load();
                    }
                    this.widenEmptyPendingOnce = false;
                }
            } catch (e) {
                this.notify('error', 'Gagal load: ' + this.errMsg(e, ''));
            } finally {
                this.loading = false;
            }
        },
        openDetail(p) {
            this.selected = JSON.parse(JSON.stringify(p));
            this.actionNote = '';
            this.detailOpen = true;
        },
        async approvePayment() {
            if (!window.confirm('Approve payment ini? Efek (aktif paket / saldo masuk / order paid) akan langsung dijalankan.')) return;
            this.actionRunning = true;
            try {
                const r = await axios.post('admin/payment/manual/' + this.selected.id + '/approve', {
                    admin_note: this.actionNote || '',
                });
                if (r.data && r.data.success) {
                    this.notify('success', 'Payment disetujui');
                    this.detailOpen = false;
                    await this.load();
                } else {
                    this.notify('error', 'Gagal: ' + (r.data && r.data.message));
                }
            } catch (e) {
                this.notify('error', 'Gagal: ' + this.errMsg(e, ''));
            } finally {
                this.actionRunning = false;
            }
        },
        async rejectPayment() {
            if (!this.actionNote || !this.actionNote.trim()) {
                this.notify('warning', 'Tulis alasan reject di field catatan');
                return;
            }
            if (!window.confirm('Reject payment ini?')) return;
            this.actionRunning = true;
            try {
                const r = await axios.post('admin/payment/manual/' + this.selected.id + '/reject', {
                    admin_note: this.actionNote,
                });
                if (r.data && r.data.success) {
                    this.notify('success', 'Payment ditolak');
                    this.detailOpen = false;
                    await this.load();
                } else {
                    this.notify('error', 'Gagal: ' + (r.data && r.data.message));
                }
            } catch (e) {
                this.notify('error', 'Gagal: ' + this.errMsg(e, ''));
            } finally {
                this.actionRunning = false;
            }
        },
    },
};
</script>

<style scoped>
.gap-2 > * + * { margin-left: 8px; }
.custom-modal-overlay {
    position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5);
    z-index: 1050; display: flex; align-items: center; justify-content: center;
}
.custom-modal-content {
    width: 100%; max-width: 900px; margin: 16px; max-height: 90vh; overflow-y: auto;
}
</style>
